import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { FONT_HEADING } from "../../../theme";
import {
  preInstallShopifyOAuth,
  buildShopifyOAuthInstallUrl,
  verifyShopifyOAuthAuth,
  revokeShopifyOAuth,
  getShopifyOAuthStores,
  claimShopifyOAuth,
} from "../../../APICalling/shopifyAdminApi";
import { deleteAllShopifyWebhooks } from "../../../APICalling/shopifyApi";
import { inactivateAllSettings } from "../../../APICalling/settingApi";
import { setUpOrgVariable } from "../../../APICalling/zohoAdminApi";
import { ensureDefaultMappingsAndWebhooks } from "../../../util/ensureDefaultSetup";
import {
  ORG_VAR_MAPS_API_NAME,
  SHOPIFY_API_KEY_API_NAME,
  SHOPIFY_SHOP_URL_HEADER_KEY,
  buildShopifyShopUrlHeader,
  buildShopifyConnName,
  SHOPIFY_ORG_CONN,
  EXTENSION_IDENTIFIER,
} from "../../../config/serviceConstant";
import {
  getOpenSnakErrorData,
  getOpenSnakSuccessData,
} from "../../../config/otherConstant";

const ZOHO = window.ZOHO;

// Shopify brand greens (matches demo_1_partner_custom_app.html)
const SHOPIFY_GREEN = "#0d6e1f";
const SHOPIFY_GREEN_HOVER = "#0a571a";
const SHOPIFY_DARK = "#04291e";
const SHOPIFY_MINT = "#9ee493";

// Valid subdomain: lowercase letters/digits/hyphens, no leading/trailing hyphen
const SHOPIFY_SUBDOMAIN_REGEX = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;

// Accept full pasted URLs / domains and reduce to the bare subdomain.
const normalizeSubdomain = (raw) =>
  (raw || "")
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/\.myshopify\.com.*$/, "")
    .replace(/\/.*$/, "")
    .replace(/\s+/g, "");

const isValidSubdomain = (sub) => SHOPIFY_SUBDOMAIN_REGEX.test(sub || "");

// Read ?shop=...&status=... from the current URL and strip them after read.
function readSuccessParams() {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search || "");
  const shop = params.get("shop");
  const status = params.get("status");
  if (!shop && !status) return null;
  return { shop, status };
}
function clearSuccessParams() {
  if (typeof window === "undefined" || !window.history?.replaceState) return;
  const url = new URL(window.location.href);
  url.searchParams.delete("shop");
  url.searchParams.delete("status");
  window.history.replaceState({}, "", url.pathname + (url.search ? "?" + url.searchParams.toString() : "") + url.hash);
}

// ── Add Store Modal — OAuth subdomain entry ──────────────────────────────────
function AddStoreModal({ open, onClose, onContinue, loading, errorMessage, installUrl, onStartOver, onShopifyOpened }) {
  const [sub, setSub] = useState("");
  const [submittedSub, setSubmittedSub] = useState("");
  const [opened, setOpened] = useState(false);
  const normalized = normalizeSubdomain(sub);
  const valid = isValidSubdomain(normalized);
  const showError = sub.length > 0 && !valid;
  const step = installUrl ? 2 : 1;
  // Backend-level rejection ("not a valid Shopify store", 404, etc.) keeps
  // Continue disabled until the user edits the input — otherwise repeated
  // clicks just re-hit the same failure.
  const backendRejectedCurrent = Boolean(errorMessage) && submittedSub === normalized;

  useEffect(() => {
    if (!open) {
      setSub("");
      setSubmittedSub("");
      setOpened(false);
    }
  }, [open]);

  useEffect(() => {
    if (!installUrl) setOpened(false);
  }, [installUrl]);

  const submit = () => {
    if (!valid || loading || backendRejectedCurrent) return;
    setSubmittedSub(normalized);
    onContinue(`${normalized}.myshopify.com`);
  };

  // Popup-window handle — we keep it so the polling layer can auto-close the
  // popup once /verifyauth confirms the install succeeded.
  const popupRef = useRef(null);

  const handleOpenPopup = () => {
    if (!installUrl) return;
    // Centered popup. The window name "shopify_install" lets repeat clicks
    // reuse the same popup instead of stacking new ones.
    const w = 540;
    const h = 720;
    const left = Math.max(0, (window.screen.availWidth - w) / 2);
    const top = Math.max(0, (window.screen.availHeight - h) / 2);
    const features = `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no,location=yes,status=no,scrollbars=yes,resizable=yes`;
    const popup = window.open(installUrl, "shopify_install", features);
    if (popup) {
      popupRef.current = popup;
      popup.focus?.();
    } else {
      // Popup blocked — fall back to a normal new tab so the flow still works.
      window.open(installUrl, "_blank", "noopener,noreferrer");
    }
    setOpened(true);
    onShopifyOpened?.(popupRef);
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "14px", overflow: "hidden", maxWidth: 560 } }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2.5,
          py: 1.75,
          borderBottom: "1px solid #e4e4e7",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box
            sx={{
              bgcolor: SHOPIFY_DARK,
              color: SHOPIFY_MINT,
              fontWeight: 700,
              fontSize: 12,
              px: "8px",
              py: "3px",
              borderRadius: "5px",
              letterSpacing: 0.5,
            }}
          >
            S
          </Box>
          <Typography sx={{ fontFamily: FONT_HEADING, fontWeight: 600, fontSize: 15 }}>
            {step === 2 ? "Open Shopify to approve install" : "Connect a Shopify store"}
          </Typography>
        </Box>
        <Box
          component="button"
          onClick={loading ? undefined : onClose}
          sx={{
            background: "none",
            border: "none",
            cursor: loading ? "not-allowed" : "pointer",
            fontSize: 17,
            color: "#71717a",
            p: "4px",
            opacity: loading ? 0.5 : 1,
          }}
        >
          &#x2715;
        </Box>
      </Box>

      {/* Body */}
      {step === 1 ? (
        <Box sx={{ p: "22px 24px" }}>
          <Typography
            sx={{ fontSize: 13, fontWeight: 600, color: "#3f3f46", mb: 0.75 }}
          >
            Your Shopify store domain
          </Typography>

          {/* Subdomain input with .myshopify.com suffix */}
          <Box sx={{ position: "relative" }}>
            <TextField
              fullWidth
              size="small"
              placeholder="yourstore"
              value={sub}
              onChange={(e) => setSub(normalizeSubdomain(e.target.value))}
              onPaste={(e) => {
                e.preventDefault();
                const text = (e.clipboardData || window.clipboardData)?.getData("text") || "";
                setSub(normalizeSubdomain(text));
              }}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              autoComplete="off"
              disabled={loading}
              error={showError}
              inputProps={{
                spellCheck: false,
                autoCapitalize: "none",
                style: { paddingRight: 120 },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontFamily: "ui-monospace, Menlo, Consolas, monospace",
                  fontSize: 14,
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: SHOPIFY_GREEN,
                  },
                },
              }}
            />
            <Typography
              sx={{
                position: "absolute",
                right: 12,
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: 13,
                color: "#71717a",
                fontFamily: "ui-monospace, Menlo, Consolas, monospace",
                pointerEvents: "none",
              }}
            >
              .myshopify.com
            </Typography>
          </Box>

          <Typography sx={{ fontSize: 12, color: showError ? "#dc2626" : "#71717a", mt: 0.75 }}>
            {showError
              ? "Use only lowercase letters, digits, and hyphens — no spaces or dots."
              : "The subdomain you use to log into Shopify. We'll prepare an install link for you to open."}
          </Typography>

          {/* What happens next info card */}
          <Box
            sx={{
              mt: 2.5,
              display: "flex",
              gap: 1.25,
              alignItems: "flex-start",
              bgcolor: "#eff6ff",
              border: "1px solid #bfdbfe",
              color: "#1d4ed8",
              borderRadius: "8px",
              p: "10px 12px",
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 17, mt: "1px", flexShrink: 0 }} />
            <Typography sx={{ fontSize: 13, lineHeight: 1.5 }}>
              <Box component="strong" sx={{ fontWeight: 700 }}>
                What happens next:
              </Box>{" "}
              Shopify will show you exactly which permissions our app needs
              (read/write customers, products, orders). Approve once and you're
              done — no token to copy or paste.
            </Typography>
          </Box>

          {/* Error banner (e.g. pre-install failure, network error) */}
          {errorMessage && (
            <Box
              sx={{
                mt: 2,
                display: "flex",
                gap: 1.25,
                alignItems: "flex-start",
                bgcolor: "#fef2f2",
                border: "1px solid #fecaca",
                color: "#991b1b",
                borderRadius: "8px",
                p: "10px 12px",
              }}
            >
              <WarningAmberOutlinedIcon sx={{ fontSize: 17, mt: "1px", flexShrink: 0 }} />
              <Typography sx={{ fontSize: 13, lineHeight: 1.5 }}>
                {errorMessage}
              </Typography>
            </Box>
          )}
        </Box>
      ) : (
        <Box sx={{ p: "22px 24px" }}>
          <Typography
            sx={{ fontSize: 13, fontWeight: 600, color: "#3f3f46", mb: 0.75 }}
          >
            Your install link is ready
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#71717a", mb: 1.5, lineHeight: 1.5 }}>
            Open this link in a new tab and approve the install on Shopify.
            You can also copy the URL and open it manually in any browser
            where you're logged into your store.
          </Typography>

          {/* URL display (read-only) */}
          <TextField
            id="shopify-install-url-input"
            fullWidth
            size="small"
            value={installUrl || ""}
            InputProps={{ readOnly: true }}
            onFocus={(e) => e.target.select()}
            inputProps={{ spellCheck: false }}
            sx={{
              "& .MuiOutlinedInput-root": {
                fontFamily: "ui-monospace, Menlo, Consolas, monospace",
                fontSize: 13,
                bgcolor: "#fafafa",
              },
            }}
          />

          <Button
            fullWidth
            variant="contained"
            onClick={handleOpenPopup}
            startIcon={<OpenInNewIcon sx={{ fontSize: 17 }} />}
            sx={{
              mt: 2,
              textTransform: "none",
              fontWeight: 600,
              bgcolor: SHOPIFY_GREEN,
              boxShadow: "none",
              "&:hover": { bgcolor: SHOPIFY_GREEN_HOVER, boxShadow: "none" },
            }}
          >
            Open Shopify
          </Button>

          {/* Info: post-approval bounce-back */}
          <Box
            sx={{
              mt: 2,
              display: "flex",
              gap: 1.25,
              alignItems: "flex-start",
              bgcolor: "#eff6ff",
              border: "1px solid #bfdbfe",
              color: "#1d4ed8",
              borderRadius: "8px",
              p: "10px 12px",
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 17, mt: "1px", flexShrink: 0 }} />
            <Typography sx={{ fontSize: 13, lineHeight: 1.5 }}>
              After approving in Shopify, you'll be redirected back. Refresh
              this page if the new store doesn't appear automatically.
            </Typography>
          </Box>
        </Box>
      )}

      {/* Footer */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2.5,
          py: 1.75,
          borderTop: "1px solid #e4e4e7",
          bgcolor: "#fafafa",
        }}
      >
        <Typography sx={{ fontSize: 12, color: "#71717a" }}>
          Step {step} of 2
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          {step === 1 ? (
            <>
              <Button
                variant="outlined"
                size="small"
                onClick={onClose}
                disabled={loading}
                sx={{
                  textTransform: "none",
                  color: "#3f3f46",
                  borderColor: "#e4e4e7",
                  "&:hover": { borderColor: "#a1a1aa", bgcolor: "#fafafa" },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                disabled={!valid || loading || backendRejectedCurrent}
                onClick={submit}
                endIcon={!loading && <ArrowForwardIcon sx={{ fontSize: 17 }} />}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  bgcolor: SHOPIFY_GREEN,
                  boxShadow: "none",
                  "&:hover": { bgcolor: SHOPIFY_GREEN_HOVER, boxShadow: "none" },
                  "&.Mui-disabled": { bgcolor: "#cbd5e1", color: "#fff" },
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={14} sx={{ color: "#fff", mr: 1 }} />
                    Preparing install link…
                  </>
                ) : (
                  "Continue"
                )}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                size="small"
                onClick={onStartOver}
                sx={{
                  textTransform: "none",
                  color: "#3f3f46",
                  borderColor: "#e4e4e7",
                  "&:hover": { borderColor: "#a1a1aa", bgcolor: "#fafafa" },
                }}
              >
                Use a different store
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={onClose}
                disabled={!opened}
                title={!opened ? "Open Shopify in a new tab first" : undefined}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  bgcolor: SHOPIFY_GREEN,
                  boxShadow: "none",
                  "&:hover": { bgcolor: SHOPIFY_GREEN_HOVER, boxShadow: "none" },
                  "&.Mui-disabled": { bgcolor: "#cbd5e1", color: "#fff" },
                }}
              >
                Done
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}

// ── Helper: info row inside a store card ──
function InfoRow({ icon, label, valueNode }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
        {icon}
        <Typography sx={{ fontSize: 12, color: "#71717a" }}>{label}</Typography>
      </Box>
      {valueNode}
    </Box>
  );
}

// ── Store Card (OAuth) ──
function StoreCard({ store, status, onVerify, onDisconnect }) {
  const statusStyles = {
    verifying: { bg: "#fef3c7", fg: "#92400e", label: "Verifying…" },
    active: { bg: "#dcfce7", fg: "#166534", label: "Active" },
    failed: { bg: "#fee2e2", fg: "#991b1b", label: "Needs reconnect" },
  };
  const s = statusStyles[status] || statusStyles.verifying;
  const sub = (store.domain || "").replace(/\.myshopify\.com$/, "");
  const initial = (sub || "S")[0].toUpperCase();
  const [menuAnchor, setMenuAnchor] = useState(null);

  return (
    <Box
      sx={{
        border: "1px solid #e4e4e7",
        borderRadius: "12px",
        p: 2,
        bgcolor: "#fff",
        display: "flex",
        flexDirection: "column",
        transition: "border-color 0.15s, box-shadow 0.15s",
        "&:hover": {
          borderColor: "#a1a1aa",
          boxShadow: "0 1px 3px 0 rgba(0,0,0,.04)",
        },
      }}
    >
      {/* Header: avatar + name + menu */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, mb: 1.5 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            bgcolor: "#ecfdf5",
            color: "#065f46",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
            fontSize: 15,
            flexShrink: 0,
          }}
        >
          {initial}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: FONT_HEADING,
              fontWeight: 600,
              fontSize: 15,
              color: "#09090b",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              letterSpacing: "-0.01em",
            }}
          >
            {sub}
          </Typography>
          <Typography
            sx={{
              fontSize: 12,
              color: "#71717a",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {store.domain}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          sx={{ color: "#71717a", p: "4px", mt: "-4px" }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          PaperProps={{ sx: { borderRadius: "8px", minWidth: 160 } }}
        >
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onVerify(store);
            }}
            sx={{ fontSize: 14 }}
          >
            Verify connection
          </MenuItem>
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onDisconnect(store);
            }}
            sx={{ fontSize: 14, color: "#dc2626" }}
          >
            Disconnect store
          </MenuItem>
        </Menu>
      </Box>

      {/* Info rows */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", mb: 2 }}>
        <InfoRow
          icon={<SwapHorizIcon sx={{ fontSize: 15, color: "#71717a" }} />}
          label="Direction"
          valueNode={
            <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#09090b" }}>
              Two-way
            </Typography>
          }
        />
        <InfoRow
          icon={<BoltOutlinedIcon sx={{ fontSize: 15, color: "#71717a" }} />}
          label="Webhooks"
          valueNode={
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                fontSize: 12,
                fontWeight: 600,
                px: "8px",
                py: "1px",
                borderRadius: "9999px",
                bgcolor: s.bg,
                color: s.fg,
              }}
            >
              {status === "verifying" && (
                <CircularProgress size={9} sx={{ color: s.fg }} />
              )}
              {status === "active" ? "12 Active" : s.label}
            </Box>
          }
        />
        <InfoRow
          icon={<AccessTimeOutlinedIcon sx={{ fontSize: 15, color: "#71717a" }} />}
          label="Last Sync"
          valueNode={
            <Typography sx={{ fontSize: 13, color: "#09090b" }}>
              Just now
            </Typography>
          }
        />
        <InfoRow
          icon={<LockOutlinedIcon sx={{ fontSize: 15, color: "#71717a" }} />}
          label="Auth Method"
          valueNode={
            <Typography sx={{ fontSize: 11, color: "#09090b", fontWeight: 500 }}>
              OAuth (rotates automatically)
            </Typography>
          }
        />
      </Box>

      {/* Manage button */}
      <Box sx={{ mt: "auto" }}>
        <Button
          fullWidth
          size="small"
          variant="outlined"
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          sx={{
            textTransform: "none",
            fontSize: 13,
            fontWeight: 600,
            color: "#3f3f46",
            borderColor: "#e4e4e7",
            "&:hover": { borderColor: "#a1a1aa", bgcolor: "#fafafa" },
          }}
        >
          Manage
        </Button>
      </Box>
    </Box>
  );
}

// ── Main Tab ──
export default function ShopifyStoresTab({
  orgId,
  apiKey,
  zApiKey,
  dataCenterUrl,
  dataCenter,
  orgVarMaps,
  setOrgVarMaps,
  shop,
  setShop,
  shopifyStores,
  setShopifyStores,
  shopifyAuth,
  setShopifyAuth,
  zohoAuth,
  onSettingsCreated,
  setSetupInProgress,
  setSnack,
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [oauthError, setOauthError] = useState("");
  const [pendingInstallUrl, setPendingInstallUrl] = useState("");
  const [pendingShop, setPendingShop] = useState("");
  // Once the user opens Shopify in a new tab, we poll /verifyauth every 15s
  // here until the token lands, then close the modal and run post-install
  // setup. The "settingUp" flag drives the blocking "Please wait..." dialog.
  const [verifyingShop, setVerifyingShop] = useState("");
  const [settingUp, setSettingUp] = useState(false);

  // Disconnect dialog target
  const [disconnectTarget, setDisconnectTarget] = useState(null);
  const [disconnecting, setDisconnecting] = useState(false);

  // Per-store verification: "verifying" | "active" | "failed"
  const [storeStatus, setStoreStatus] = useState({});

  // Success-route detection: when OAuth bounces user back with ?shop=&status=connected
  const [successBanner, setSuccessBanner] = useState(null); // { shop, status }
  const [pendingBanner, setPendingBanner] = useState(null); // { shop } for status=pending

  // Stores are prefetched at app init and flow down as a prop.
  const stores = shopifyStores || [];
  const setStores = setShopifyStores;

  // Per-store headers — connname is the per-shop slug so each token is
  // individually addressable.
  const buildShopifyAuthHeaders = (shopDomain) => ({
    orgid: orgId,
    connname: buildShopifyConnName(shopDomain),
    apikey: apiKey,
    datacenterurl: dataCenterUrl,
    datacenter: dataCenter,
    [SHOPIFY_SHOP_URL_HEADER_KEY]: buildShopifyShopUrlHeader(shopDomain),
  });

  // Headers for the ecommerce/service routes — extension-level connname,
  // per-store scope via shopifyshopurl.
  const buildShopifyServiceHeaders = (shopDomain) => ({
    orgid: orgId,
    connname: EXTENSION_IDENTIFIER,
    apikey: apiKey,
    datacenterurl: dataCenterUrl,
    datacenter: dataCenter,
    [SHOPIFY_SHOP_URL_HEADER_KEY]: buildShopifyShopUrlHeader(shopDomain),
  });

  // Org-level headers for list-all calls — used by /tokens.
  const orgHeaders = {
    orgid: orgId,
    connname: SHOPIFY_ORG_CONN,
    apikey: apiKey,
    datacenterurl: dataCenterUrl,
    datacenter: dataCenter,
  };

  // On mount: detect OAuth success bounce-back via URL params.
  useEffect(() => {
    const params = readSuccessParams();
    if (!params) return;
    if (params.status === "connected" && params.shop) {
      setSuccessBanner({ shop: params.shop });
      // After ~1.5s, fade out — but always refetch tokens immediately.
      setTimeout(() => setSuccessBanner(null), 1500);
    } else if (params.status === "pending" && params.shop) {
      // pre-install cache expired before Shopify finished its OAuth round-trip
      setPendingBanner({ shop: params.shop });
    }
    clearSuccessParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch stores when this tab mounts so the list is fresh.
  useEffect(() => {
    if (!orgId || !apiKey) return;
    let cancelled = false;
    (async () => {
      try {
        const fresh = await getShopifyOAuthStores(orgHeaders);
        if (cancelled) return;
        setStores(fresh || []);
        setShopifyAuth((fresh || []).length > 0);
      } catch {
        // keep existing prefetched list on failure
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, apiKey, dataCenterUrl, dataCenter]);

  // Verify each store's token in parallel whenever the list changes.
  useEffect(() => {
    if (!stores.length) {
      setStoreStatus({});
      return;
    }
    const domains = stores.map((s) => s.domain).filter(Boolean);
    setStoreStatus((prev) => {
      const next = {};
      domains.forEach((d) => {
        next[d] = prev[d] === "active" || prev[d] === "failed" ? prev[d] : "verifying";
      });
      return next;
    });
    let cancelled = false;
    domains.forEach(async (domain) => {
      try {
        const { valid } = await verifyShopifyOAuthAuth(
          domain,
          buildShopifyAuthHeaders(domain)
        );
        if (cancelled) return;
        setStoreStatus((prev) => ({
          ...prev,
          [domain]: valid ? "active" : "failed",
        }));
      } catch {
        if (cancelled) return;
        setStoreStatus((prev) => ({ ...prev, [domain]: "failed" }));
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stores, orgId, apiKey, dataCenterUrl, dataCenter]);

  // After a brand-new store lands, create default settings + webhooks for it.
  // Zoho CRM is the only target service. Scoped to the new store.
  const runDefaultSetupForNewStore = async (storeDomain) => {
    const services = [];
    if (zohoAuth) services.push("Zoho CRM");
    if (services.length === 0) return;

    const perStoreShopifyHeaders = buildShopifyServiceHeaders(storeDomain);
    const perStoreSettingAuth = { orgId, apiKey, dataCenterUrl, shop: storeDomain };

    let totalCreated = 0;
    for (const service of services) {
      try {
        const { created, warnings } = await ensureDefaultMappingsAndWebhooks(
          perStoreSettingAuth,
          perStoreShopifyHeaders,
          storeDomain,
          zApiKey,
          service
        );
        if (created.length > 0) totalCreated += created.length;
        if (warnings.length > 0) {
          console.warn(
            `[NewStoreSetup] Warnings for ${service} @ ${storeDomain}:`,
            warnings
          );
        }
      } catch (err) {
        console.error(
          `[NewStoreSetup] Error for ${service} @ ${storeDomain}:`,
          err
        );
      }
    }

    if (totalCreated > 0) onSettingsCreated?.();
  };

  // Triggered by AddStoreModal: pre-install, then surface the install URL so
  // the merchant can open it in a new tab or copy it manually.
  const handleContinueToShopify = async (storeDomain) => {
    if (!storeDomain) return;
    setOauthLoading(true);
    setOauthError("");

    // Headers scoped to the NEW store domain — connname is per-shop slug.
    const newStoreHeaders = buildShopifyAuthHeaders(storeDomain);

    const { ok, error } = await preInstallShopifyOAuth(
      storeDomain,
      newStoreHeaders
    );
    if (!ok) {
      setOauthLoading(false);
      setOauthError(error || "Failed to register the install. Please try again.");
      return;
    }

    setPendingShop(storeDomain);
    setPendingInstallUrl(buildShopifyOAuthInstallUrl(storeDomain));
    setOauthLoading(false);
  };

  const closeAddStoreModal = () => {
    // Fire-and-forget claim against the backend so it can finalize / consume
    // the pre-install cache entry for the shop the merchant just opened.
    if (pendingShop) {
      claimShopifyOAuth(pendingShop, buildShopifyAuthHeaders(pendingShop)).catch(
        (err) => console.error("[Shopify OAuth] claim on modal close failed:", err)
      );
    }
    setModalOpen(false);
    setOauthError("");
    setPendingInstallUrl("");
    setPendingShop("");
    setVerifyingShop("");
  };

  const startOverAddStoreModal = () => {
    setPendingInstallUrl("");
    setPendingShop("");
    setOauthError("");
    setVerifyingShop("");
  };

  // Persist OAuth result into org vars, refresh stores, bootstrap default
  // sync rules + webhooks. Shared by the OAuth bounce-back path and the
  // verifyauth polling path so both flows converge on the same setup.
  const runPostInstallFlow = async (newShop) => {
    try {
      const updatedMaps = {
        ...(orgVarMaps || {}),
        shopifyAuth: true,
        shopifyStoreDomain: newShop,
      };
      await setUpOrgVariable(
        ZOHO,
        ORG_VAR_MAPS_API_NAME,
        JSON.stringify(updatedMaps)
      );
      await setUpOrgVariable(ZOHO, SHOPIFY_API_KEY_API_NAME, "__oauth__");
      setOrgVarMaps(updatedMaps);

      const fresh = await getShopifyOAuthStores(orgHeaders);
      setStores(fresh || []);
      setShop?.(newShop);
      setShopifyAuth(true);

      setSetupInProgress?.(true);
      try {
        await runDefaultSetupForNewStore(newShop);
      } finally {
        setSetupInProgress?.(false);
      }

      setSnack(getOpenSnakSuccessData(`${newShop} is connected.`));
    } catch (err) {
      console.error("[OAuth] post-connect setup failed:", err);
    }
  };

  // Holds a reference to the Shopify-install popup window so the polling
  // effect can close it the moment /verifyauth confirms the new token.
  const installPopupRef = useRef(null);

  // Called by the modal once the merchant clicks "Open Shopify".
  // Kicks off the polling loop scoped to that shop and stashes the popup
  // handle so we can auto-close it after a successful install.
  const handleShopifyOpened = (popupRef) => {
    installPopupRef.current = popupRef?.current || null;
    if (pendingShop) setVerifyingShop(pendingShop);
  };

  // Poll /verifyauth every 15s while waiting for the merchant to approve.
  // As soon as a valid token lands, close the install popup + modal,
  // show the "Please wait" dialog, and run the shared post-install flow.
  useEffect(() => {
    if (!verifyingShop) return;
    const shop = verifyingShop;
    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      try {
        const { valid } = await verifyShopifyOAuthAuth(
          shop,
          buildShopifyAuthHeaders(shop)
        );
        if (cancelled || !valid) return;
        // Auto-close the install popup window if it's still open.
        try {
          if (installPopupRef.current && !installPopupRef.current.closed) {
            installPopupRef.current.close();
          }
        } catch {
          // Cross-origin can throw; safe to ignore.
        }
        installPopupRef.current = null;
        // Stop polling and tear down the install modal.
        setVerifyingShop("");
        setModalOpen(false);
        setPendingInstallUrl("");
        setPendingShop("");
        setSettingUp(true);
        try {
          await runPostInstallFlow(shop);
        } finally {
          setSettingUp(false);
        }
      } catch {
        // Transient errors — keep polling.
      }
    };

    const interval = setInterval(tick, 15000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifyingShop]);

  // When the OAuth bounce-back lands and we detect ?status=connected, refetch
  // the tokens list + bootstrap default sync rules for the new store.
  useEffect(() => {
    if (!successBanner?.shop) return;
    runPostInstallFlow(successBanner.shop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successBanner?.shop]);

  // Per-store verification triggered from the menu.
  const handleVerifyStore = async (store) => {
    if (!store?.domain) return;
    setStoreStatus((prev) => ({ ...prev, [store.domain]: "verifying" }));
    const { valid, error } = await verifyShopifyOAuthAuth(
      store.domain,
      buildShopifyAuthHeaders(store.domain)
    );
    setStoreStatus((prev) => ({
      ...prev,
      [store.domain]: valid ? "active" : "failed",
    }));
    if (valid) {
      setSnack(getOpenSnakSuccessData(`${store.domain} connection is healthy.`));
    } else {
      setSnack(
        getOpenSnakErrorData(
          error || `${store.domain} token is no longer valid.`,
          6000
        )
      );
    }
  };

  const requestDisconnect = (store) => {
    if (!store?.domain) return;
    setDisconnectTarget(store);
  };

  const cleanupStoreSettingsAndWebhooks = async (store) => {
    const storeHeaders = buildShopifyServiceHeaders(store.domain);
    try {
      await Promise.all([
        deleteAllShopifyWebhooks(storeHeaders),
        inactivateAllSettings(storeHeaders),
      ]);
    } catch (err) {
      console.error("[Disconnect] Cleanup error:", err);
    }
  };

  const handleDisconnectStore = async (store) => {
    if (!store?.domain) return;
    setDisconnecting(true);
    try {
      await cleanupStoreSettingsAndWebhooks(store);

      const { ok, error } = await revokeShopifyOAuth(
        store.domain,
        buildShopifyAuthHeaders(store.domain)
      );
      if (!ok) {
        setSnack(
          getOpenSnakErrorData(
            error || `Failed to disconnect "${store.name || store.domain}".`,
            5000
          )
        );
        return;
      }

      const updatedStores = stores.filter((s) => s.domain !== store.domain);
      const updatedMaps = {
        ...(orgVarMaps || {}),
        stores: updatedStores,
        shopifyAuth: updatedStores.length > 0,
        shopifyStoreDomain:
          updatedStores.length > 0 ? updatedStores[0].domain : "",
      };
      await setUpOrgVariable(
        ZOHO,
        ORG_VAR_MAPS_API_NAME,
        JSON.stringify(updatedMaps)
      );
      if (updatedStores.length === 0) {
        await setUpOrgVariable(ZOHO, SHOPIFY_API_KEY_API_NAME, "");
      }
      setOrgVarMaps(updatedMaps);
      setStores(updatedStores);
      setShop?.(updatedStores[0]?.domain || "");
      if (updatedStores.length === 0) setShopifyAuth(false);
      setSnack(
        getOpenSnakSuccessData(
          `Store "${store.name || store.domain}" disconnected.`
        )
      );
    } finally {
      setDisconnecting(false);
      setDisconnectTarget(null);
    }
  };

  const handleRefresh = async () => {
    try {
      const fresh = await getShopifyOAuthStores(orgHeaders);
      setStores(fresh || []);
      setSnack(getOpenSnakSuccessData("Stores refreshed."));
    } catch {
      setSnack(getOpenSnakErrorData("Could not refresh stores."));
    }
  };

  // ── Stage 1: empty state (no stores yet) ───────────────────────────────
  if (!shopifyAuth && stores.length === 0) {
    return (
      <>
        {pendingBanner && <PendingBanner shop={pendingBanner.shop} />}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "calc(100vh - 160px)",
            textAlign: "center",
            px: 3,
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "12px",
              bgcolor: SHOPIFY_DARK,
              color: SHOPIFY_MINT,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2.5,
              fontSize: 30,
              fontWeight: 800,
            }}
          >
            S
          </Box>
          <Typography
            sx={{
              fontFamily: FONT_HEADING,
              fontSize: 20,
              fontWeight: 700,
              color: "#09090b",
              mb: "6px",
              letterSpacing: "-0.02em",
            }}
          >
            Connect Your Shopify Store
          </Typography>
          <Typography
            sx={{
              fontSize: 14,
              color: "#71717a",
              mb: 3,
              maxWidth: 380,
              lineHeight: 1.55,
            }}
          >
            One click. We'll redirect you to Shopify, you approve the
            permissions, and you're back here in 15 seconds.
          </Typography>
          <Button
            variant="contained"
            onClick={() => setModalOpen(true)}
            endIcon={<ArrowForwardIcon sx={{ fontSize: 17 }} />}
            sx={{
              textTransform: "none",
              px: "22px",
              py: "10px",
              fontSize: 14,
              fontWeight: 600,
              bgcolor: SHOPIFY_GREEN,
              boxShadow: "none",
              "&:hover": { bgcolor: SHOPIFY_GREEN_HOVER, boxShadow: "none" },
            }}
          >
            Install on Shopify
          </Button>
        </Box>

        <AddStoreModal
          open={modalOpen}
          onClose={closeAddStoreModal}
          onContinue={handleContinueToShopify}
          loading={oauthLoading}
          errorMessage={oauthError}
          installUrl={pendingInstallUrl}
          onStartOver={startOverAddStoreModal}
          onShopifyOpened={handleShopifyOpened}
        />

        {/* Blocking "Please wait..." dialog while we wire up default settings */}
        <Dialog
          open={settingUp}
          PaperProps={{
            sx: { borderRadius: "14px", maxWidth: 420, width: "100%", p: 3 },
          }}
          disableEscapeKeyDown
        >
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 1.5 }}>
            <CircularProgress size={36} sx={{ color: SHOPIFY_GREEN }} />
            <Typography
              sx={{
                fontFamily: FONT_HEADING,
                fontSize: 16,
                fontWeight: 700,
                color: "#09090b",
                mt: 0.5,
              }}
            >
              Please wait a moment
            </Typography>
            <Typography sx={{ fontSize: 14, color: "#52525b", lineHeight: 1.55 }}>
              We're making everything automated for you — creating default sync
              rules and webhooks for your new store.
            </Typography>
          </Box>
        </Dialog>
      </>
    );
  }

  // ── Authenticated state — grid + banner + add button ───────────────────
  const allActive =
    stores.length > 0 &&
    stores.every((st) => storeStatus[st.domain] === "active");
  const anyFailed = stores.some((st) => storeStatus[st.domain] === "failed");
  const anyVerifying = stores.some(
    (st) => !storeStatus[st.domain] || storeStatus[st.domain] === "verifying"
  );

  const banner = successBanner
    ? {
        bg: "#ecfdf5",
        border: "#a7f3d0",
        fg: "#065f46",
        title: `${successBanner.shop} is connected`,
        subtitle:
          "Default sync rules and webhooks are live. New customers, orders, and products will flow into Zoho CRM automatically.",
      }
    : anyFailed
    ? {
        bg: "#fef2f2",
        border: "#fecaca",
        fg: "#991b1b",
        title: "One or more stores need attention",
        subtitle: "Re-authorise any store showing 'Needs reconnect' below.",
      }
    : anyVerifying
    ? {
        bg: "#fffbeb",
        border: "#fde68a",
        fg: "#92400e",
        title: "Verifying store connections",
        subtitle: "Hold on while we confirm each store with Shopify.",
      }
    : allActive
    ? {
        bg: "#ecfdf5",
        border: "#a7f3d0",
        fg: "#065f46",
        title: "All stores are connected and active",
        subtitle: "Your Shopify stores are in sync with Zoho CRM.",
      }
    : {
        bg: "#f4f4f5",
        border: "#e4e4e7",
        fg: "#3f3f46",
        title: "Shopify connection ready",
        subtitle: "You can now add and manage stores.",
      };

  return (
    <>
      {pendingBanner && <PendingBanner shop={pendingBanner.shop} />}

      {/* Top action row */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mb: 2 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon sx={{ fontSize: 17 }} />}
          onClick={handleRefresh}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: 14,
            color: "#3f3f46",
            borderColor: "#e4e4e7",
            "&:hover": { borderColor: "#a1a1aa", bgcolor: "#fafafa" },
          }}
        >
          Refresh
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon sx={{ fontSize: 17 }} />}
          onClick={() => setModalOpen(true)}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: 14,
            bgcolor: SHOPIFY_GREEN,
            boxShadow: "none",
            "&:hover": { bgcolor: SHOPIFY_GREEN_HOVER, boxShadow: "none" },
          }}
        >
          Install Another Store
        </Button>
      </Box>

      {/* Status banner — transparent: no fill or border, the per-state icon
          and text colours alone carry the status. */}
      <Box
        sx={{
          bgcolor: "transparent",
          p: "4px 2px",
          mb: 3,
          display: "flex",
          alignItems: "flex-start",
          gap: 1.25,
        }}
      >
        <CheckCircleIcon sx={{ fontSize: 18, color: banner.fg, mt: "2px" }} />
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: banner.fg }}>
            {banner.title}
          </Typography>
          <Typography sx={{ fontSize: 13, color: banner.fg, opacity: 0.9, mt: "2px" }}>
            {banner.subtitle}
          </Typography>
        </Box>
      </Box>

      {/* Section heading */}
      <Box sx={{ mb: 2 }}>
        <Typography
          sx={{
            fontFamily: FONT_HEADING,
            fontSize: 17,
            fontWeight: 700,
            letterSpacing: "-0.02em",
            color: "#09090b",
          }}
        >
          Connected Shopify Stores ({stores.length})
        </Typography>
        <Typography sx={{ fontSize: 13, color: "#71717a", mt: "2px" }}>
          Manage your Shopify store connections
        </Typography>
      </Box>

      {/* Empty state or grid */}
      {stores.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 6,
            px: 3,
            color: "#71717a",
            border: "1px dashed #e4e4e7",
            borderRadius: "12px",
            bgcolor: "#fff",
          }}
        >
          <StorefrontOutlinedIcon sx={{ fontSize: 36, color: "#a1a1aa", mb: 1 }} />
          <Typography sx={{ fontFamily: FONT_HEADING, fontSize: 16, fontWeight: 600, color: "#09090b", mb: "6px" }}>
            No stores connected yet
          </Typography>
          <Typography sx={{ fontSize: 14 }}>
            Click "Install Another Store" to connect your first Shopify store.
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
            gap: 2,
          }}
        >
          {stores.map((store, i) => (
            <StoreCard
              key={store.domain + i}
              store={store}
              status={storeStatus[store.domain]}
              onVerify={handleVerifyStore}
              onDisconnect={requestDisconnect}
            />
          ))}
          {/* Add another store tile (dashed) */}
          <Box
            sx={{
              border: "1px dashed #cbd5e1",
              borderRadius: "12px",
              p: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              gap: 1.25,
              minHeight: 240,
              bgcolor: "#fafafa",
              transition: "all 0.15s",
              "&:hover": { borderColor: SHOPIFY_GREEN, bgcolor: "#f4f4f5" },
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                bgcolor: "#fff",
                border: "1px solid #e4e4e7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#0f172a",
              }}
            >
              <AddIcon sx={{ fontSize: 18 }} />
            </Box>
            <Box>
              <Typography sx={{ fontFamily: FONT_HEADING, fontSize: 15, fontWeight: 600, color: "#09090b" }}>
                Add Another Store
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#71717a", mt: "2px", maxWidth: 180 }}>
                One-click install via Shopify OAuth.
              </Typography>
            </Box>
            <Button
              size="small"
              variant="contained"
              onClick={() => setModalOpen(true)}
              sx={{
                textTransform: "none",
                fontSize: 13,
                fontWeight: 600,
                bgcolor: SHOPIFY_GREEN,
                boxShadow: "none",
                "&:hover": { bgcolor: SHOPIFY_GREEN_HOVER, boxShadow: "none" },
              }}
            >
              Install on Shopify
            </Button>
          </Box>
        </Box>
      )}

      <AddStoreModal
        open={modalOpen}
        onClose={closeAddStoreModal}
        onContinue={handleContinueToShopify}
        loading={oauthLoading}
        errorMessage={oauthError}
        installUrl={pendingInstallUrl}
        onStartOver={startOverAddStoreModal}
        onShopifyOpened={handleShopifyOpened}
      />

      {/* Blocking "Please wait..." dialog while we wire up default settings */}
      <Dialog
        open={settingUp}
        PaperProps={{
          sx: { borderRadius: "14px", maxWidth: 420, width: "100%", p: 3 },
        }}
        disableEscapeKeyDown
      >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 1.5 }}>
          <CircularProgress size={36} sx={{ color: SHOPIFY_GREEN }} />
          <Typography
            sx={{
              fontFamily: FONT_HEADING,
              fontSize: 16,
              fontWeight: 700,
              color: "#09090b",
              mt: 0.5,
            }}
          >
            Please wait a moment
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#52525b", lineHeight: 1.55 }}>
            We're making everything automated for you — creating default sync
            rules and webhooks for your new store.
          </Typography>
        </Box>
      </Dialog>

      {/* Disconnect confirmation */}
      <Dialog
        open={!!disconnectTarget}
        onClose={() => !disconnecting && setDisconnectTarget(null)}
        PaperProps={{
          sx: { borderRadius: "14px", maxWidth: 460, width: "100%", overflow: "hidden" },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              bgcolor: "#fee2e2",
              color: "#dc2626",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2,
            }}
          >
            <WarningAmberOutlinedIcon sx={{ fontSize: 26 }} />
          </Box>

          <Typography
            sx={{
              fontFamily: FONT_HEADING,
              fontSize: 17,
              fontWeight: 700,
              color: "#09090b",
              letterSpacing: "-0.02em",
              mb: 0.5,
            }}
          >
            Disconnect{" "}
            <Box component="span" sx={{ color: "#0f172a" }}>
              {disconnectTarget?.name || disconnectTarget?.domain || "this store"}
            </Box>
            ?
          </Typography>

          <Typography sx={{ fontSize: 14, color: "#52525b", lineHeight: 1.55, mb: 2 }}>
            This will revoke the OAuth token for this store. The following will
            happen:
          </Typography>

          <Box
            sx={{
              bgcolor: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: "10px",
              p: "12px 14px",
              mb: 2.5,
            }}
          >
            {[
              "All webhooks for this store will be removed from Shopify",
              "Sync rules for this store will be disabled",
              "Other connected stores will keep running normally",
            ].map((line, i) => (
              <Box
                key={i}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  fontSize: 14,
                  color: "#991b1b",
                  py: "3px",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 4,
                    height: 4,
                    borderRadius: "50%",
                    bgcolor: "#dc2626",
                    flexShrink: 0,
                    mt: "7px",
                  }}
                />
                {line}
              </Box>
            ))}
          </Box>

          <Typography sx={{ fontSize: 13, color: "#71717a", mb: 2.5 }}>
            Existing Zoho CRM records won't be deleted. You can reconnect this
            store later via OAuth.
          </Typography>

          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button
              onClick={() => setDisconnectTarget(null)}
              variant="outlined"
              disabled={disconnecting}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 14,
                color: "#3f3f46",
                borderColor: "#e4e4e7",
                "&:hover": { borderColor: "#a1a1aa", bgcolor: "#fafafa" },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleDisconnectStore(disconnectTarget)}
              variant="contained"
              disabled={disconnecting}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 14,
                bgcolor: "#dc2626",
                boxShadow: "none",
                "&:hover": { bgcolor: "#b91c1c", boxShadow: "none" },
              }}
            >
              {disconnecting && (
                <CircularProgress size={14} sx={{ mr: 1, color: "#fff" }} />
              )}
              Disconnect Store
            </Button>
          </Box>
        </Box>
      </Dialog>
    </>
  );
}

// Banner shown when Shopify bounces back with status=pending, meaning the
// 15-minute pre-install cache TTL expired before the OAuth round-trip finished.
function PendingBanner({ shop }) {
  return (
    <Box
      sx={{
        bgcolor: "#fffbeb",
        border: "1px solid #fde68a",
        borderRadius: "10px",
        p: "10px 14px",
        mb: 2,
        display: "flex",
        alignItems: "flex-start",
        gap: 1.25,
      }}
    >
      <WarningAmberOutlinedIcon sx={{ fontSize: 17, color: "#92400e", mt: "2px" }} />
      <Typography sx={{ fontSize: 14, color: "#92400e", lineHeight: 1.5 }}>
        <Box component="strong" sx={{ fontWeight: 700 }}>
          Connection registered but not linked.
        </Box>{" "}
        The OAuth session for <code>{shop}</code> expired before Shopify
        finished. Click <em>Install on Shopify</em> again to retry.
      </Typography>
    </Box>
  );
}
